import React, { useState, useEffect, FormEvent, ChangeEvent } from "react";
import "./ProjectEditModal.css";
import { formatDateFrench, parseDateFromFrench } from "../utils/dateFormatting";
import {
  EffortBreakdown,
  ProjectTask,
  ProjectEditModalProps,
  WORK_CATEGORY_LABELS,
  WorkCategory,
  Status,
  STATUS_LABELS,
} from "../types";
import ProjectTasksModal from "./ProjectTasksModal";
import { CommentsModal } from "./CommentsModal";
import { useAuthContext } from "../context/AuthContext";

export type EffortData = {
  [key in WorkCategory]: string;
};

interface FormData {
  name: string;
  startDate: string;
  endDate: string;
  efforts: EffortData;
  status: Status;
  clientProjectIds: string[];
}

const ProjectEditModal: React.FC<ProjectEditModalProps> = ({
  project,
  isOpen,
  onClose,
  onSave,
  onDelete,
  availableProjects = [],
  availableClientProjects = [],
  onMoveTask,
}) => {
  const { canEdit, user, canCreateComments, canViewComments } = useAuthContext();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    startDate: "",
    endDate: "",
    efforts: {
      [WorkCategory.DEVELOPMENT]: "",
      [WorkCategory.IA]: "",
      [WorkCategory.PROJECT_MANAGEMENT]: "",
      [WorkCategory.CONTENT_CREATION]: "",
      [WorkCategory.COG_SCI]: "",
    },
    status: Status.BACKLOG,
    clientProjectIds: [],
  });
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);

  const isNewProject = project && !project.id;

  useEffect(() => {
    if (project) {
      const efforts: EffortData = Object.values(WorkCategory).reduce(
        (acc, key) => {
          acc[key] = String(project.effortBreakdown[key]);
          return acc;
        },
        {} as EffortData,
      );
      setFormData({
        name: project.name,
        startDate: project.startDate ? formatDateFrench(project.startDate) : "",
        endDate: project.endDate ? formatDateFrench(project.endDate) : "",
        efforts,
        status: project.status || Status.BACKLOG,
        clientProjectIds: project.clientProjectIds || [],
      });
    }
  }, [project]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Check if this is an effort field (WorkCategory)
    if (Object.values(WorkCategory).includes(name as WorkCategory)) {
      setFormData((prev) => ({
        ...prev,
        efforts: {
          ...prev.efforts,
          [name as WorkCategory]: value,
        },
      }));
    } else {
      // Handle other fields (name, startDate, endDate, status)
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!canEdit || !project) return;

    const effortBreakdown: EffortBreakdown = Object.values(WorkCategory).reduce(
      (acc, key) => {
        acc[key] = parseFloat(formData.efforts[key]);
        return acc;
      },
      {} as EffortBreakdown,
    );

    const startDateISO = parseDateFromFrench(formData.startDate);
    const endDateISO = parseDateFromFrench(formData.endDate);

    const updatedProject = project.update({
      name: formData.name,
      startDate: startDateISO ? new Date(startDateISO) : undefined,
      endDate: endDateISO ? new Date(endDateISO) : undefined,
      effortBreakdown,
      clientProjectIds: formData.clientProjectIds,
    });

    updatedProject.status = formData.status;

    onSave(updatedProject);
  };

  const parseFrenchDateSafe = (frenchDate: string): Date | null => {
    const iso = parseDateFromFrench(frenchDate);
    if (!iso) return null;

    const [year, month, day] = iso.split("-").map(Number);
    if (!year || !month || !day) return null;

    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  const handleStartNow = () => {
    if (!canEdit) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentStart = parseFrenchDateSafe(formData.startDate);
    const currentEnd = parseFrenchDateSafe(formData.endDate);

    let nextEnd = new Date(today);

    if (currentStart && currentEnd) {
      const millisecondsPerDay = 1000 * 60 * 60 * 24;
      const durationDays = Math.round(
        (currentEnd.getTime() - currentStart.getTime()) / millisecondsPerDay,
      );
      nextEnd.setDate(nextEnd.getDate() + Math.max(0, durationDays));
    } else {
      nextEnd.setMonth(nextEnd.getMonth() + 1);
    }

    setFormData((prev) => ({
      ...prev,
      startDate: formatDateFrench(today),
      endDate: formatDateFrench(nextEnd),
      status:
        prev.status === Status.BACKLOG
          ? Status.UNDER_DISCUSSION
          : prev.status,
    }));
  };

  const handleSaveTasks = (tasks: ProjectTask[]) => {
    if (!project) return;
    const updatedProject = project.update({ tasks });
    updatedProject.status = project.status;
    onSave(updatedProject, { keepModalOpen: true });
    setIsTasksModalOpen(false);
  };

  const getManageTasksDisabledReason = (): string | null => {
    if (isNewProject) {
      return "Save the project first before managing tasks.";
    }

    const allowedStatuses = [Status.PLANNED, Status.IN_PROGRESS, Status.DONE];
    if (!allowedStatuses.includes(formData.status)) {
      return "Tasks can only be managed when project status is Planned, In Progress, or Done.";
    }

    if (!formData.startDate || !formData.endDate) {
      return "Set both start date and end date before managing tasks.";
    }

    return null;
  };

  const manageTasksDisabledReason = getManageTasksDisabledReason();

  const handleClientProjectToggle = (clientProjectId: string) => {
    setFormData((prev) => {
      const exists = prev.clientProjectIds.includes(clientProjectId);
      return {
        ...prev,
        clientProjectIds: exists
          ? prev.clientProjectIds.filter((id) => id !== clientProjectId)
          : [...prev.clientProjectIds, clientProjectId],
      };
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isNewProject
              ? "Add New Project"
              : canEdit
                ? "Edit Project"
                : "View Project"}
          </h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        {!canEdit && !isNewProject && (
          <div className="readonly-banner" role="note">
            Read-only mode: details are visible, but editing is disabled.
          </div>
        )}

        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-group">
            <label htmlFor="name">Project Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!canEdit}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={!canEdit}
              required
            >
              {Object.values(Status).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="text"
                id="startDate"
                name="startDate"
                placeholder="DD/MM/YYYY"
                value={formData.startDate}
                onChange={handleChange}
                disabled={!canEdit}
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="text"
                id="endDate"
                name="endDate"
                placeholder="DD/MM/YYYY"
                value={formData.endDate}
                onChange={handleChange}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="form-actions-row">
            <button
              type="button"
              className="start-now-button"
              disabled={!canEdit}
              onClick={handleStartNow}
              title="Set start date to today and adjust end date"
            >
              Start now
            </button>
          </div>

          <div className="form-row">
            {Object.entries(WorkCategory).map(([label, type], _) => (
              <div className="form-group" key={String(label)}>
                <label htmlFor={type}>{WORK_CATEGORY_LABELS[type]}</label>
                <input
                  type="number"
                  id={type}
                  name={type}
                  value={formData.efforts[type]}
                  onChange={handleChange}
                  disabled={!canEdit}
                  min="0"
                  step="1"
                  required
                />
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>Client projects</label>
            {availableClientProjects.length === 0 ? (
              <p className="client-projects-empty-message">
                No client project defined yet.
              </p>
            ) : (
              <div className="client-projects-checkbox-list">
                {availableClientProjects.map((clientProject) => (
                  <label key={clientProject.id} className="client-project-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.clientProjectIds.includes(clientProject.id)}
                      onChange={() => handleClientProjectToggle(clientProject.id)}
                      disabled={!canEdit}
                    />
                    <span>{clientProject.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <div className="form-actions-left">
              {canEdit &&
                !isNewProject &&
                onDelete &&
                project &&
                project.id && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => onDelete(project.id as number)}
                  >
                    Delete Project
                  </button>
                )}
            </div>
            <div className="form-actions-right">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              {!isNewProject && (
                <>
                  <div className="manage-tasks-wrapper">
                    <button
                      type="button"
                      className="cancel-button"
                      disabled={!canEdit || Boolean(manageTasksDisabledReason)}
                      onClick={() => setIsTasksModalOpen(true)}
                      title={
                        !manageTasksDisabledReason
                          ? "Manage weekly tasks"
                          : undefined
                      }
                    >
                      Manage Tasks
                    </button>
                    {manageTasksDisabledReason && (
                      <span className="disabled-reason-tooltip" role="tooltip">
                        {manageTasksDisabledReason}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="cancel-button"
                    disabled={!project || !project.id}
                    onClick={() => setIsCommentsModalOpen(true)}
                    title="View and add comments on this project"
                  >
                    💬 Comments
                  </button>
                </>
              )}
              <button type="submit" className="save-button" disabled={!canEdit}>
                {isNewProject ? "Add Project" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {project && (
        <ProjectTasksModal
          project={project}
          isOpen={isTasksModalOpen}
          onClose={() => setIsTasksModalOpen(false)}
          onSave={handleSaveTasks}
          availableProjects={availableProjects.filter(
            (candidate) => candidate.id !== project.id,
          )}
          onMoveTask={
            onMoveTask
              ? (task: ProjectTask, targetProjectId: number) =>
                  onMoveTask(project.id as number, task, targetProjectId)
              : undefined
          }
        />
      )}

      {project && (
        <CommentsModal
          isOpen={isCommentsModalOpen}
          taskId={`project_${project.id}`}
          taskName={project.name}
          canCreateComment={canCreateComments}
          canViewComments={canViewComments}
          onClose={() => setIsCommentsModalOpen(false)}
          user={user}
        />
      )}
    </div>
  );
};

export default ProjectEditModal;
